import type { Desktop } from "../core/Desktop.js";
import type { KeyEventArgs } from "../core/events.js";
import { Button } from "./Button.js";
import { Label } from "./Label.js";
import { Window, type WindowOptions } from "./Window.js";

export type DialogResult = "ok" | "cancel" | "yes" | "no" | "retry" | "abort" | "none";

export interface DialogOptions extends WindowOptions {
  /** Center when shown - over `owner` when there is one, else over the work area. Defaults to true. */
  center?: boolean;
}

/**
 * A modal window.
 *
 * `showDialog` resolves with the result once the dialog closes, so callers can
 * `await` it. While it is open the Desktop rejects input everywhere else and
 * flashes the dialog's title bar instead.
 */
export class Dialog extends Window {
  #resolve: ((result: DialogResult) => void) | null = null;
  #result: DialogResult = "none";
  #center: boolean;

  /** Activated by Enter; typically the OK button. */
  defaultButton: Button | null = null;
  /** Activated by Escape; typically Cancel. */
  cancelButton: Button | null = null;

  constructor(options: DialogOptions = {}) {
    super({
      // Win32 gives a dialog a fixed single border, and so does this: the
      // style carries the fixed size, so there is no separate `resizable`
      // default to keep in step with it. A caller who asks for a resizable
      // dialog and says nothing about the border gets the frame that can
      // actually be dragged, because the two together mean nothing else.
      borderStyle: options.resizable === true ? "sizable" : "single",
      // Never a minimize or maximize button, whatever the border style: a
      // modal the user can send to the taskbar is a modal they cannot answer.
      showMinimize: false,
      showMaximize: false,
      showClose: true,
      ...options,
    });
    this.#center = options.center ?? true;
    // Closing via the caption X counts as canceling.
    this.on("closed", () => this.#settle());
  }

  override get modal(): boolean {
    return true;
  }

  get result(): DialogResult {
    return this.#result;
  }

  /** Show modally and resolve once closed. */
  showDialog(desktop: Desktop): Promise<DialogResult> {
    this.#result = "none";
    // Callers may attach the dialog early to lay its contents out against a
    // real theme; adding twice would just churn the z-order.
    if (this.parent !== desktop) desktop.addWindow(this);
    else desktop.activate(this);
    this.visible = true;
    if (this.#center) this.centerOn(desktop);
    desktop.pushModal(this);
    if (this.defaultButton?.focusable) this.defaultButton.focus();
    else desktop.focusManager.restoreIn(this);
    return new Promise<DialogResult>((resolve) => {
      this.#resolve = resolve;
    });
  }

  centerOn(desktop: Desktop): void {
    // Center within the work area so a taskbar never covers the buttons.
    const area = desktop.workArea;
    const owner = this.owner;
    if (owner && owner.desktop === desktop && owner.visible && !owner.minimized) {
      // WinForms' CenterParent, held inside the work area.
      const o = owner.bounds;
      const x = Math.round(o.x + (o.width - this.width) / 2);
      const y = Math.round(o.y + (o.height - this.height) / 2);
      this.setLocation(
        Math.max(area.x, Math.min(x, area.x + area.width - this.width)),
        Math.max(area.y, Math.min(y, area.y + area.height - this.height)),
      );
      return;
    }
    this.setLocation(
      Math.max(area.x, Math.round(area.x + (area.width - this.width) / 2)),
      Math.max(area.y, Math.round(area.y + (area.height - this.height) / 3)),
    );
  }

  /** Set the result and close. */
  closeWith(result: DialogResult): void {
    this.#result = result;
    if (!this.close()) this.#result = "none";
  }

  override close(): boolean {
    const desktop = this.desktop;
    const closed = super.close();
    if (closed) desktop?.popModal(this);
    return closed;
  }

  #settle(): void {
    const resolve = this.#resolve;
    this.#resolve = null;
    resolve?.(this.#result);
  }

  /** Enter and Escape, after the focused control has declined them. */
  override handleDialogKey(args: KeyEventArgs): boolean {
    if (args.key === "Enter" && this.defaultButton?.effectivelyEnabled) {
      this.defaultButton.performClick();
      return true;
    }
    if (args.key === "Escape") {
      if (this.cancelButton?.effectivelyEnabled) this.cancelButton.performClick();
      else this.closeWith("cancel");
      return true;
    }
    return false;
  }
}

export interface MessageBoxOptions {
  title?: string;
  message: string;
  /** Which buttons to show, left to right. Defaults to a single OK. */
  buttons?: readonly DialogResult[];
  /** Which button Enter activates. Defaults to the first. */
  defaultResult?: DialogResult;
  /** Which button Escape activates. Defaults to cancel/no if present. */
  cancelResult?: DialogResult;
  width?: number;
}

const BUTTON_LABELS: Record<DialogResult, string> = {
  ok: "OK",
  cancel: "Cancel",
  yes: "Yes",
  no: "No",
  retry: "Retry",
  abort: "Abort",
  none: "Close",
};

/**
 * Build and show a standard message dialog. The buttons are laid out
 * right-aligned along the bottom, the way Windows dialogs do.
 */
export async function messageBox(
  desktop: Desktop,
  options: MessageBoxOptions,
): Promise<DialogResult> {
  const theme = desktop.theme;
  const m = theme.metrics;
  const pad = m.dialogPadding;
  const gap = m.dialogButtonGap;
  const buttons = options.buttons ?? ["ok"];
  const width = options.width ?? 380;

  // Measure the message so the dialog grows to fit it.
  const maxTextWidth = width - pad * 2;
  const lines = desktop.rc.wrapText(options.message, m.font, maxTextWidth);
  const lineHeight = desktop.rc.lineHeight(m.font);
  const textHeight = Math.max(lineHeight, lines.length * lineHeight);

  const buttonHeight = m.controlHeight;
  const contentHeight = pad + textHeight + pad + buttonHeight + pad;

  const dialog = new Dialog({
    title: options.title ?? "",
    width,
    height: contentHeight,
    minWidth: 200,
    minHeight: 100,
    visible: false,
  });
  // Attach first: clientRect and the frame size both come from the theme, and
  // a detached window has neither.
  desktop.addWindow(dialog);
  dialog.setClientSize(width, contentHeight);

  lines.forEach((line, i) => {
    dialog.controls.add(
      new Label({
        x: pad,
        y: pad + i * lineHeight,
        width: maxTextWidth,
        height: lineHeight,
        text: line,
        verticalAlign: "middle",
        ellipsis: false,
      }),
    );
  });

  const client = dialog.clientRect;
  const buttonY = client.height - pad - buttonHeight;
  const widths = buttons.map((b) =>
    Math.max(
      m.buttonMinWidth,
      Math.ceil(
        desktop.rc.measureText(BUTTON_LABELS[b], m.font).width +
          m.buttonPadding.left +
          m.buttonPadding.right,
      ),
    ),
  );
  const totalWidth = widths.reduce((a, b) => a + b, 0) + gap * (buttons.length - 1);
  let x = client.width - pad - totalWidth;

  const defaultResult = options.defaultResult ?? buttons[0]!;
  const cancelResult =
    options.cancelResult ??
    (buttons.includes("cancel") ? "cancel" : buttons.includes("no") ? "no" : undefined);

  buttons.forEach((result, i) => {
    const button = new Button({
      x,
      y: buttonY,
      width: widths[i]!,
      height: buttonHeight,
      text: BUTTON_LABELS[result],
      isDefault: result === defaultResult,
      anchor: { left: false, top: false, right: true, bottom: true },
      onClick: () => dialog.closeWith(result),
    });
    dialog.controls.add(button);
    if (result === defaultResult) dialog.defaultButton = button;
    if (result === cancelResult) dialog.cancelButton = button;
    x += widths[i]! + gap;
  });

  const answer = await dialog.showDialog(desktop);
  // The caption X and Escape both mean "no choice made"; map that to cancel.
  return answer === "none" ? (cancelResult ?? "cancel") : answer;
}

/** Greedy word wrap against the desktop's text measurement. */
